/**
 * pm-gallery.js - mobile swipe progress + full-screen lightbox.
 *
 * DESIGN NOTES
 * ------------
 * The desktop vertical stack is pure CSS and needs none of this. Everything
 * here exists for the two interactive parts:
 *
 *   1. Progress bar and "n / m" counter under the mobile carousel, read from
 *      the scroll position of the real list so it stays correct however wide
 *      the images end up.
 *
 *   2. A lightbox that clones the live <img> nodes into an overlay instead of
 *      re-rendering URLs from Liquid: one request per image, and the viewer
 *      shows exactly the pixels the page chose.
 *
 * A11y, and why it is not a native <dialog>
 * ------------------------------------------
 * Escape closes, arrows navigate, Home/End jump, focus is trapped while open
 * and returned to the button that opened it on close. Native <dialog> was
 * rejected because Dawn already ships a modal with its own focus behaviour,
 * and two competing top-layer focus traps on one page fight each other.
 *
 * No-op safety: every entry point returns early when its markup is absent, so
 * the file is safe to load on any template.
 */

(function () {
  'use strict';

  function q(sel, root) { return (root || document).querySelector(sel); }
  function qa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* ----------------------------------------------------------------------
     ONE VIEWER AT A TIME
     ----------------------------------------------------------------------
     This theme has TWO independent image viewers on every product page:

       1. pm-lightbox  - this file, opened by the .pm-gallery__open wrapper.
       2. <product-modal> - Dawn's stock media modal, opened by the
          <modal-opener>/<button class="product__media-toggle"> that
          product-thumbnail.liquid renders INSIDE that same wrapper.

     They shared one click target and nothing coordinated them, so a single
     click fired both handlers: this lightbox opened AND Dawn's modal opened
     on top of it. Whichever one the visitor dismissed, the other stayed
     mounted - which is the reported "second, stuck" viewer. Dawn's ModalDialog
     also owns `document.body.classList.add('overflow-hidden')` in show() and
     only clears it in hide(); when its close path was never reached the page
     kept a scroll lock, so mobile (no ESC key) trapped the visitor.

     closeCompetingViewers() is the single arbiter: exactly one viewer may be
     open, and whichever opens makes the other one close through its OWN public
     API, so each keeps ownership of its own body classes and focus trap.
     ---------------------------------------------------------------------- */
  function closeCompetingViewers() {
    // Dawn modals (product media, and any other modal-dialog on the page).
    qa('modal-dialog[open], product-modal[open]').forEach(function (modal) {
      if (typeof modal.hide === 'function') modal.hide();
    });
  }

  function dawnViewerOpen() {
    return !!q('modal-dialog[open], product-modal[open]');
  }

  // Triggers already wired, so re-running init() cannot stack a second
  // listener on the same element. See the note on init() below.
  var wired = new WeakSet();

  /* ----------------------------------------------------------------------
     MOBILE CAROUSEL: progress + counter
     ---------------------------------------------------------------------- */
  function initCarousel() {
    var list = q('.product__media-wrapper .product__media-list');
    if (!list) return;

    var bar = q('[data-pm-progress-bar]');
    var counter = q('[data-pm-gallery-counter]');
    var items = qa(':scope > li', list);
    if (!items.length) return;

    function nearestIndex() {
      var listLeft = list.getBoundingClientRect().left;
      var target = listLeft + list.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < items.length; i++) {
        var r = items[i].getBoundingClientRect();
        var d = Math.abs((r.left + r.width / 2) - target);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    function update() {
      var i = nearestIndex();
      if (bar) bar.style.setProperty('--pm-progress', String((i + 1) / items.length));
      if (counter) {
        counter.textContent = (i + 1) + ' / ' + items.length;
        counter.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + items.length);
      }
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    list.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ----------------------------------------------------------------------
     LIGHTBOX
     ---------------------------------------------------------------------- */
  function initLightbox() {
    var box = q('[data-pm-lightbox]');
    var stage = q('[data-pm-lightbox-stage]');
    var triggers = qa('.pm-gallery__open');
    if (!box || !stage || !triggers.length) return;

    var counter = q('[data-pm-lightbox-counter]', box);
    var prevBtn = q('[data-pm-lightbox-prev]', box);
    var nextBtn = q('[data-pm-lightbox-next]', box);
    var images = [];
    var index = 0;
    var opener = null;

    // Build the slide list from the live gallery. currentSrc is copied so the
    // overlay shows the exact rendition the browser already picked; src is
    // the fallback for an image that has not decoded yet.
    triggers.forEach(function (btn) {
      var img = q('img', btn);
      if (img) images.push({ src: img.currentSrc || img.src, alt: img.alt || '' });
    });
    if (!images.length) return;

    function render() {
      var item = images[index];
      if (!item) return;
      stage.innerHTML = '';
      var img = document.createElement('img');
      img.src = item.src;
      img.alt = item.alt;
      stage.appendChild(img);
      if (counter) counter.textContent = (index + 1) + ' / ' + images.length;
      // One image needs no arrows.
      if (prevBtn) prevBtn.hidden = images.length < 2;
      if (nextBtn) nextBtn.hidden = images.length < 2;
    }

    function open(i, from) {
      // Only one viewer at a time. Dawn's product modal is opened by a
      // <modal-opener> nested inside this very trigger, so a single click
      // reaches BOTH handlers. Close Dawn's through its own hide() so it
      // releases its own `overflow-hidden` body class and focus trap rather
      // than leaving them stranded.
      closeCompetingViewers();

      index = i;
      opener = from || null;
      render();
      box.dataset.open = 'true';
      box.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pm-lightbox-open');
      var closeBtn = q('[data-pm-lightbox-close]', box);
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      // Idempotent: closing an already-closed viewer must not touch focus or
      // re-run cleanup, so a double close (button + bubbled ESC) is harmless.
      if (box.dataset.open !== 'true') return;

      box.dataset.open = 'false';
      box.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pm-lightbox-open');
      // Drop the cloned <img>s immediately: they are the only nodes this
      // viewer creates, so emptying the stage is the complete teardown.
      stage.innerHTML = '';

      var back = opener;
      opener = null;
      // Return focus only to something still in the document, and only when
      // no other viewer has taken over in the meantime.
      if (back && document.contains(back) && !dawnViewerOpen()) back.focus();
    }

    function step(delta) {
      index = (index + delta + images.length) % images.length;
      render();
    }

    triggers.forEach(function (btn, i) {
      if (wired.has(btn)) return;
      wired.add(btn);

      // THE ROOT CAUSE FIX.
      //
      // product-thumbnail.liquid renders Dawn's
      //   <modal-opener data-modal="#ProductModal-..."> containing a native
      //   <button class="product__media-toggle">
      // INSIDE this .pm-gallery__open wrapper. So one physical click matched
      // two independent openers:
      //
      //   pm-gallery.js  -> open()                (this lightbox)
      //   ModalOpener    -> modal.show(button)    (Dawn's product-modal)
      //
      // Both ran on the same event, so two full-screen viewers mounted at
      // once. Dawn's <product-modal> carries z-index 101 against this
      // lightbox's 60, so Dawn's sat ON TOP; the visitor dismissed the one
      // they could see and the other stayed behind it. That is the reported
      // "a second viewer appears / remains". Worse, Dawn's ModalDialog adds
      // `overflow-hidden` to <body> in show() and only removes it in hide(),
      // so an unreachable modal left the page scroll-locked - unescapable on
      // mobile, which has no ESC key.
      //
      // CAPTURE phase is required, not bubble. Dawn's ModalOpener binds its
      // listener to the nested <button>, and that button is the real click
      // target (it is absolutely positioned over the image). A bubble-phase
      // stopPropagation on this wrapper runs AFTER that listener has already
      // called modal.show(). Capturing here stops the event before it ever
      // reaches the button, so Dawn's opener is never invoked at all.
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        open(i, btn);
      }, true);

      // The wrapper is a <div role="button">, not a native <button>, so it does
      // NOT get Enter/Space activation for free the way a real button would.
      // Without this a keyboard user can reach the plate with Tab but cannot
      // open the viewer from it. stopPropagation for the same reason as above.
      btn.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        // Space would otherwise scroll the page.
        e.preventDefault();
        e.stopPropagation();
        open(i, btn);
      });
    });

    // Box-level and document-level listeners are registered once per page,
    // not once per init(). init() re-runs on every product-info:loaded
    // (i.e. every variant change), and each re-run built fresh closures, so
    // the ESC handler and the close-button handlers stacked up: N variant
    // changes meant N keydown listeners and N click handlers firing on one
    // close. Guarded so repeated open/close and variant switching can never
    // accumulate duplicate listeners.
    if (!box.__pmWired) {
      box.__pmWired = true;

      qa('[data-pm-lightbox-close]', box).forEach(function (el) {
        el.addEventListener('click', close);
      });
      if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

      document.addEventListener('keydown', function (e) {
        if (box.dataset.open !== 'true') return;

        if (e.key === 'Escape') {
          e.preventDefault();
          // Stop the event here. Dawn's ModalDialog also listens for ESC
          // (on keyup, bound to the modal element) and other overlays may
          // listen on document; stopping propagation guarantees a single
          // close action rather than a cascade of them.
          e.stopPropagation();
          close();
          return;
        }
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); step(1); return; }
        if (e.key === 'Home') { e.preventDefault(); index = 0; render(); return; }
        if (e.key === 'End') { e.preventDefault(); index = images.length - 1; render(); return; }

        if (e.key === 'Tab') {
          var focusables = qa('button:not([hidden])', box).filter(function (el) {
            return el.offsetParent !== null;
          });
          if (!focusables.length) return;
          var first = focusables[0];
          var last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      });

      // Safety net. If any other viewer on the page is still mounted once
      // ours has closed, the visitor is trapped behind it - the exact bug
      // being fixed. Closing through each owner's own API releases its own
      // scroll lock and focus trap, so nothing is left over the product page.
      new MutationObserver(function () {
        if (box.dataset.open === 'true') return;
        if (dawnViewerOpen()) closeCompetingViewers();
      }).observe(document.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'data-open'],
      });
    }
  }

  function init() {
    initCarousel();
    initLightbox();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Dawn replaces the media list after a variant change; re-read so the
  // counter does not go stale.
  document.addEventListener('product-info:loaded', init);
})();
